<template>
  <div>
    <h3>Content</h3>

    <div v-if="isLoading" class="text-center pt-3">
      <b-spinner label="Loading..." />
    </div>
    <template v-else>
      <div class="d-flex justify-content-end mb-1">
        <b-button
          variant="primary"
          size="sm"
          @click="openModal(null)"
        >
          <span>Add content</span>
        </b-button>
      </div>
      <div class="custom-search d-flex flex-wrap align-items-end">
        <b-form-group class="mb-1 mr-1">
          <b-form-input
            v-model="searchTerm"
            placeholder="Search"
            type="text"
            class="d-inline-block"
          />
        </b-form-group>
        <b-form-group
          label="Scheduled From"
          label-for="content-schedule-from"
          class="mb-1 mr-1 content-schedule-filter"
        >
          <b-form-datepicker
            id="content-schedule-from"
            ref="scheduleFromPicker"
            v-model="scheduleFrom"
            locale="en"
            size="sm"
            placeholder="From"
            reset-button
            :date-format-options="{ year: 'numeric', month: 'short', day: 'numeric' }"
            @input="onScheduleFromPicked"
            @hidden="onScheduleFromHidden"
          />
        </b-form-group>
        <b-form-group
          label="Scheduled To"
          label-for="content-schedule-to"
          class="mb-1 mr-1 content-schedule-filter"
        >
          <b-form-datepicker
            id="content-schedule-to"
            ref="scheduleToPicker"
            v-model="scheduleTo"
            locale="en"
            size="sm"
            placeholder="To"
            reset-button
            :date-format-options="{ year: 'numeric', month: 'short', day: 'numeric' }"
          />
        </b-form-group>
        <b-form-group
          label="Interval"
          label-for="content-schedule-interval"
          class="mb-1 mr-1 content-schedule-interval"
        >
          <b-form-select
            id="content-schedule-interval"
            v-model="scheduleInterval"
            :options="scheduleIntervalOptions"
            size="sm"
            @change="onScheduleIntervalChange"
          />
        </b-form-group>
        <b-form-group
          label="Channel"
          label-for="content-filter-channel"
          class="mb-1 mr-1 content-channel-filter"
        >
          <ImageTextSelect
            id="content-filter-channel"
            v-model="filterChannelId"
            :options="channelFilterOptions"
            image-key="icon"
            :clearable="true"
            placeholder="Channel"
          />
        </b-form-group>
        <b-form-group
          label="Assigned to"
          label-for="content-filter-assigned"
          class="mb-1 mr-1 content-assigned-filter"
        >
          <ImageTextSelect
            id="content-filter-assigned"
            v-model="filterAssignedId"
            :options="staffFilterOptions"
            image-key="picture"
            :clearable="true"
            placeholder="All"
          />
        </b-form-group>
        <b-form-group class="mb-1 mr-1 content-processing-filter">
          <b-form-checkbox
            id="content-processing-only"
            v-model="processingPostsOnly"
            switch
            class="mt-50"
          >
            Processing posts
          </b-form-checkbox>
        </b-form-group>
        <b-form-group class="mb-1">
          <b-button
            variant="outline-secondary"
            size="sm"
            @click="resetFilters"
          >
            Reset
          </b-button>
        </b-form-group>
      </div>
      <vue-good-table
        mode="remote"
        :columns="columns"
        :rows="tableRows"
        :total-rows="totalRows"
        :is-loading.sync="isTableLoading"
        :pagination-options="{
          enabled: true,
          perPage: pageLength,
        }"
        @page-change="onPageChange"
      >
        <template #table-row="props">
          <span v-if="props.column.field === 'name'" class="d-flex align-items-start">
            <img
              v-if="contentPreviewUrl(props.row)"
              :src="contentPreviewUrl(props.row)"
              class="table-row-thumb mr-50"
              alt=""
            >
            <span class="d-flex flex-column">
              <span class="content-name-text">{{ props.row.name }}</span>
              <b-button
                v-if="showDetailsButton(props.row)"
                variant="outline-primary"
                size="sm"
                class="align-self-start mt-50"
                @click="openPreviewModal(props.row)"
              >
                {{ detailsButtonLabel(props.row) }}
              </b-button>
              <span
                v-if="contentCategoryName(props.row)"
                class="content-category-label align-self-start mt-50"
              >
                {{ contentCategoryName(props.row) }}
              </span>
            </span>
          </span>
          <span v-else-if="props.column.field === 'channel'" class="d-flex flex-column content-channel-cell">
            <span class="d-flex align-items-center content-channel-header">
              <img
                v-if="props.row.social_resource_type?.icon"
                :src="props.row.social_resource_type.icon"
                class="content-channel-type-icon"
                alt=""
              >
              <a
                v-if="channelUrl(props.row)"
                :href="channelUrl(props.row)"
                target="_blank"
                rel="noopener noreferrer"
                class="content-channel-name"
                v-b-tooltip.hover="channelNameTooltip(props.row)"
              >
                {{ truncatedChannelName(props.row) }}
              </a>
              <span
                v-else
                class="content-channel-name"
                v-b-tooltip.hover="channelNameTooltip(props.row)"
              >{{ truncatedChannelName(props.row, '—') }}</span>
            </span>
            <span
              v-if="props.row.id_key"
              class="d-flex align-items-center content-channel-id-key-row"
            >
              <a
                v-if="idKeyUrl(props.row)"
                :href="idKeyUrl(props.row)"
                target="_blank"
                rel="noopener noreferrer"
                class="content-channel-id-key"
              >
                {{ props.row.id_key }}
              </a>
              <span
                v-else
                class="content-channel-id-key content-channel-id-key--muted"
              >
                {{ props.row.id_key }}
              </span>
              <feather-icon
                v-if="props.row.id_key"
                icon="CopyIcon"
                size="14"
                class="content-channel-id-key-copy text-secondary cursor-pointer ml-25"
                v-b-tooltip.hover
                title="Click: copy ID key. Double-click: copy content URL"
                @click.stop="onContentIdKeyCopyClick(props.row)"
                @dblclick.stop="onContentIdKeyCopyDblClick(props.row)"
              />
            </span>
          </span>
          <span v-else-if="props.column.field === 'posts'" class="content-posts-cell">
            <div
              v-for="entry in visibleContentPosts(props.row)"
              :key="entry.post.id"
              class="d-flex align-items-center content-post-row"
              :class="{ 'content-post-row--continuation': entry.inChain && !entry.isTip }"
            >
              <div class="content-post-main d-flex align-items-center flex-wrap min-w-0">
                <img
                  v-if="postResourceIcon(entry.post)"
                  :src="postResourceIcon(entry.post)"
                  class="content-post-type-icon"
                  alt=""
                >
                <a
                  v-if="entry.post.resource?.name && postResourceUrl(entry.post)"
                  :href="postResourceUrl(entry.post)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="content-post-resource-name"
                >
                  {{ entry.post.resource.name }}
                </a>
                <span
                  v-else-if="entry.post.resource?.name"
                  class="content-post-resource-name"
                >
                  {{ entry.post.resource.name }}
                </span>
                <a
                  v-if="entry.post.url"
                  :href="entry.post.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="content-post-name"
                  :class="{
                    'content-post-removed': Number(entry.post.removed) === 1,
                    'content-post-name--empty-key': !String(entry.post.id_key || '').trim(),
                    'content-post-name--has-key': Boolean(String(entry.post.id_key || '').trim()),
                  }"
                  v-b-tooltip.hover="postNameTooltip(entry.post)"
                >
                  -
                  <span
                    v-if="entry.partNumber"
                    class="content-post-part"
                  >{{ entry.partNumber }})</span>
                  {{ truncatedPostName(entry.post) }}
                </a>
                <span
                  v-else
                  class="content-post-name"
                  :class="{
                    'content-post-removed': Number(entry.post.removed) === 1,
                    'content-post-name--empty-key': !String(entry.post.id_key || '').trim(),
                    'content-post-name--has-key': Boolean(String(entry.post.id_key || '').trim()),
                  }"
                  v-b-tooltip.hover="postNameTooltip(entry.post)"
                >
                  -
                  <span
                    v-if="entry.partNumber"
                    class="content-post-part"
                  >{{ entry.partNumber }})</span>
                  {{ truncatedPostName(entry.post) }}
                </span>
                <img
                  v-if="postAssigneePicture(entry.post)"
                  :src="postAssigneePicture(entry.post)"
                  class="content-post-assignee-avatar ml-25"
                  alt=""
                  v-b-tooltip.hover
                  :title="postAssigneeName(entry.post)"
                >
                <feather-icon
                  v-if="postHasCaption(entry.post)"
                  icon="AlignLeftIcon"
                  size="14"
                  class="text-info cursor-pointer ml-25"
                  v-b-tooltip.hover
                  title="Edit caption"
                  @click="openEditPost(entry.post)"
                />
                <feather-icon
                  v-if="postHasHashtags(entry.post)"
                  icon="HashIcon"
                  size="14"
                  class="text-info cursor-pointer ml-25"
                  v-b-tooltip.hover
                  title="Edit hashtags"
                  @click="openEditPost(entry.post)"
                />
                <span
                  v-if="entry.post.post_type?.name"
                  class="content-post-type-name"
                >
                  - {{ entry.post.post_type.name }}
                </span>
                <feather-icon
                  v-if="entry.showExpandToggle"
                  :icon="entry.expanded ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                  size="14"
                  class="text-secondary cursor-pointer content-post-expand-icon ml-25 flex-shrink-0"
                  v-b-tooltip.hover
                  :title="entry.expanded ? 'Collapse playlist' : 'Expand playlist'"
                  @click="toggleContinuationExpand(entry.rootId)"
                />
              </div>
              <span class="content-post-actions d-flex align-items-center flex-shrink-0">
                <span
                  v-if="formatPostSchedule(entry.post)"
                  class="content-post-schedule mr-50"
                >
                  {{ formatPostSchedule(entry.post) }}
                </span>
                <feather-icon
                  v-if="postIsRemoved(entry.post)"
                  icon="RotateCcwIcon"
                  size="14"
                  class="text-success cursor-pointer mx-25"
                  v-b-tooltip.hover
                  title="Restore post"
                  @click="restorePostRow(entry.post)"
                />
                <feather-icon
                  v-else-if="postHasUrl(entry.post)"
                  icon="SlashIcon"
                  size="14"
                  class="text-danger cursor-pointer mx-25"
                  v-b-tooltip.hover
                  title="Mark as removed"
                  @click="markPostRemoved(entry.post)"
                />
                <feather-icon
                  v-else
                  icon="TrashIcon"
                  size="14"
                  class="text-danger cursor-pointer mx-25"
                  v-b-tooltip.hover
                  title="Delete post"
                  @click="deletePostRow(entry.post)"
                />
                <feather-icon
                  icon="Edit2Icon"
                  size="14"
                  class="text-primary cursor-pointer mx-25"
                  v-b-tooltip.hover
                  title="Edit post"
                  @click="openEditPost(entry.post)"
                />
                <feather-icon
                  icon="CopyIcon"
                  size="14"
                  class="text-secondary cursor-pointer mx-25"
                  v-b-tooltip.hover
                  title="Copy post name"
                  @click="copyPostName(entry.post)"
                />
                <feather-icon
                  v-if="entry.isTip"
                  icon="CornerDownRightIcon"
                  size="14"
                  class="text-success cursor-pointer content-post-continue-icon"
                  v-b-tooltip.hover
                  title="Add continuation post"
                  @click="openContinuationPost(entry.post, props.row)"
                />
                <span
                  v-else-if="entry.inChain"
                  class="content-post-continue-spacer"
                  aria-hidden="true"
                />
              </span>
            </div>
            <div v-if="!(props.row.posts || []).length" class="text-muted small">
              No posts
            </div>
          </span>
          <span v-else-if="props.column.field === 'action'" class="action-link-group">
            <b-dropdown
              variant="link"
              toggle-class="text-decoration-none"
              no-caret
              right
            >
              <template v-slot:button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="text-body align-middle"
                />
              </template>
              <b-dropdown-item @click="openModal(props.row)">
                <feather-icon icon="Edit2Icon" class="mr-50" />
                <span>View / Edit</span>
              </b-dropdown-item>
              <b-dropdown-item @click="openAddPost(props.row)">
                <feather-icon icon="PlusIcon" class="mr-50" />
                <span>Add post</span>
              </b-dropdown-item>
              <b-dropdown-item
                v-if="!(props.row.posts || []).length"
                @click="deleteRow(props.row)"
              >
                <feather-icon icon="TrashIcon" class="mr-50" />
                <span>Delete</span>
              </b-dropdown-item>
            </b-dropdown>
          </span>
          <DatatableCellValue
            v-else
            :field="props.column.field"
            :value="props.formattedRow[props.column.field]"
          />
        </template>

        <template #pagination-bottom="props">
          <TablePagination
            :value="tablePage"
            :total-rows="totalRows"
            :per-page="pageLength"
            @input="(value) => props.pageChanged({ currentPage: value })"
            @per-page-change="onPerPageChange"
          />
        </template>
      </vue-good-table>
    </template>

    <ManageContent
      :click-date="clickDate"
      :record="record"
      @saved="onContentSaved"
    />

    <ManagePost
      :click-date="postClickDate"
      :record="postRecord"
      :lock-content-id="postLockContentId"
      @saved="onPostSaved"
    />

    <b-modal
      v-model="showPreviewModal"
      size="lg"
      centered
      hide-footer
      @hidden="onPreviewModalHidden"
    >
      <template #modal-title>
        <span class="d-inline-flex align-items-center">
          <feather-icon
            v-if="previewIsYoutube && previewYoutubeId && !previewPlaying"
            icon="PlayCircleIcon"
            size="22"
            class="text-danger cursor-pointer mr-50 flex-shrink-0"
            v-b-tooltip.hover
            title="Play video"
            @click="playPreviewYoutube"
          />
          <span>{{ previewTitle }}</span>
        </span>
      </template>
      <div class="text-center">
        <div
          v-if="previewPlaying && previewYoutubeId"
          class="content-preview-youtube"
          :class="{ 'mb-1': previewNotes }"
        >
          <iframe
            :src="previewYoutubeEmbedUrl"
            title="YouTube video"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowfullscreen
          />
        </div>
        <img
          v-else-if="previewImageUrl"
          :src="previewImageUrl"
          alt="Preview"
          class="img-fluid"
          :class="{ 'mb-1': previewNotes }"
        >
        <div v-else-if="!previewNotes" class="text-muted">
          No preview available
        </div>
      </div>
      <div
        v-if="previewNotes"
        class="content-details-notes"
        :class="{ 'mt-1': previewImageUrl || previewPlaying }"
      >
        <h6 class="mb-50">Notes</h6>
        <div class="text-left white-space-pre-wrap">{{ previewNotes }}</div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import 'vue-good-table-next/dist/vue-good-table-next.css'
import { VueGoodTable } from 'vue-good-table-next'
import {
  BButton,
  BDropdown,
  BDropdownItem,
  BFormCheckbox,
  BFormDatepicker,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BModal,
  BSpinner,
} from 'bootstrap-vue'
import { mapActions, mapGetters } from 'vuex'
import TablePagination from '@/views/Components/Elements/TablePagination.vue'
import DatatableCellValue from '@/views/Components/Elements/DatatableCellValue.vue'
import { toastFailure, toastSuccess } from '@/libs/toastification'
import {
  formatCompanyDateTime,
  companyTodayYmd,
  companyTz,
  moment,
  parseApiInstant,
  toUtcIso,
} from '@/libs/companyTime'
import { normalizeImageUrl } from '@/libs/utils'
import ManageContent from '@/views/Modules/crtcreator/Components/ManageContent.vue'
import ManagePost from '@/views/Modules/crtcreator/Components/ManagePost.vue'
import ImageTextSelect from '@/views/Components/Elements/ImageTextSelect.vue'

export default {
  components: {
    TablePagination,
    DatatableCellValue,
    ManageContent,
    ManagePost,
    ImageTextSelect,
    BButton,
    BDropdown,
    BDropdownItem,
    BFormCheckbox,
    BFormDatepicker,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BModal,
    BSpinner,
    VueGoodTable,
  },
  data() {
    return {
      record: null,
      clickDate: null,
      postRecord: null,
      postClickDate: null,
      postLockContentId: null,
      isLoading: true,
      isTableLoading: false,
      filtersReady: false,
      searchTerm: '',
      searchDebounceTimer: null,
      scheduleFrom: null,
      scheduleTo: null,
      scheduleInterval: '',
      applyingSchedulePreset: false,
      openToAfterFromClose: false,
      filterChannelId: null,
      filterAssignedId: null,
      processingPostsOnly: false,
      tablePage: 1,
      pageLength: 25,
      loadSeq: 0,
      contentIdKeyCopyTimer: null,
      showPreviewModal: false,
      previewImageUrl: null,
      previewNotes: '',
      previewTitle: 'Details and Notes',
      previewIsYoutube: false,
      previewYoutubeId: null,
      previewPlaying: false,
      expandedContinuationRoots: {},
      columns: [
        { label: 'Name', field: 'name', width: '28%', thClass: 'content-name-col', tdClass: 'content-name-col' },
        { label: 'Channel', field: 'channel', sortable: false },
        { label: 'Posts', field: 'posts', sortable: false },
        { label: 'Actions', field: 'action', thClass: 'text-center', tdClass: 'text-center', sortable: false },
      ],
    }
  },
  computed: {
    ...mapGetters({
      items: 'crtcreator/content/items',
      totalRows: 'crtcreator/content/total',
      channels: 'crtcreator/channels/items',
      staff: 'staff/staff',
    }),
    channelFilterOptions() {
      return (this.channels || []).map((channel) => ({
        id: channel.id,
        name: channel.name,
        icon: null,
      }))
    },
    staffFilterOptions() {
      return (this.staff || []).map((member) => ({
        id: member.id,
        name: member.name,
        picture: normalizeImageUrl(member.picture),
      }))
    },
    scheduleIntervalOptions() {
      const options = [
        { value: '', text: 'All dates' },
        { value: 'today', text: 'Today' },
        { value: 'yesterday', text: 'Yesterday' },
        { value: 'last_7_days', text: 'Last 7 days' },
        { value: 'last_14_days', text: 'Last 14 days' },
        { value: 'last_30_days', text: 'Last 30 days' },
        { value: 'last_60_days', text: 'Last 60 days' },
        { value: 'last_90_days', text: 'Last 90 days' },
      ]
      if (this.scheduleInterval === 'custom') {
        options.push({ value: 'custom', text: 'Custom interval' })
      }
      return options
    },
    tableRows() {
      return (this.items || []).map((row) => this.mapRow(row))
    },
    previewYoutubeEmbedUrl() {
      if (!this.previewYoutubeId) return null
      return `https://www.youtube.com/embed/${encodeURIComponent(this.previewYoutubeId)}?autoplay=1`
    },
  },
  watch: {
    searchTerm() {
      if (!this.filtersReady) return
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer)
      }
      this.searchDebounceTimer = setTimeout(() => {
        this.tablePage = 1
        this.reloadContent()
      }, 300)
    },
    scheduleFrom() {
      if (!this.filtersReady) return
      this.syncScheduleIntervalFromDates()
      if (this.applyingSchedulePreset) return
      this.tablePage = 1
      this.reloadContent()
    },
    scheduleTo() {
      if (!this.filtersReady) return
      this.syncScheduleIntervalFromDates()
      if (this.applyingSchedulePreset) return
      this.tablePage = 1
      this.reloadContent()
    },
    filterChannelId() {
      if (!this.filtersReady) return
      this.tablePage = 1
      this.reloadContent()
    },
    filterAssignedId() {
      if (!this.filtersReady) return
      this.tablePage = 1
      this.reloadContent()
    },
    processingPostsOnly() {
      if (!this.filtersReady) return
      this.tablePage = 1
      this.reloadContent()
    },
  },
  async mounted() {
    try {
      await Promise.all([
        this.loadChannels(),
        this.loadContentCategories(),
        this.loadResources(),
        this.loadPostTypes(),
        this.loadStaff().catch(() => null),
      ])
      await this.reloadContent()
    } catch (e) {
      toastFailure('Can not load content')
    }
    this.isLoading = false
    this.filtersReady = true
  },
  beforeDestroy() {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer)
    }
    if (this.contentIdKeyCopyTimer) {
      clearTimeout(this.contentIdKeyCopyTimer)
      this.contentIdKeyCopyTimer = null
    }
  },
  methods: {
    ...mapActions({
      loadItems: 'crtcreator/content/loadItems',
      deleteItem: 'crtcreator/content/deleteItem',
      loadChannels: 'crtcreator/channels/loadItems',
      loadContentCategories: 'crtcreator/contentCategories/loadItems',
      loadResources: 'crtcreator/resources/loadItems',
      loadPostTypes: 'crtcreator/postTypes/loadItems',
      loadStaff: 'staff/loadStaff',
      updatePost: 'crtcreator/posts/updateItem',
      deletePost: 'crtcreator/posts/deleteItem',
    }),
    mapRow(row) {
      return {
        ...row,
        channel_name: row.channel?.name || '',
      }
    },
    toYmd(value) {
      if (value === null || value === undefined || value === '') {
        return null
      }
      if (typeof value === 'string') {
        const match = value.trim().match(/^(\d{4}-\d{2}-\d{2})/)
        return match ? match[1] : null
      }
      if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return moment(value).tz(companyTz()).format('YYYY-MM-DD')
      }
      return null
    },
    todayYmd() {
      return companyTodayYmd()
    },
    resolveScheduleIntervalRange(preset) {
      const today = moment.tz(companyTz()).startOf('day')
      switch (preset) {
        case 'today':
          return {
            from: today.format('YYYY-MM-DD'),
            to: today.format('YYYY-MM-DD'),
          }
        case 'yesterday': {
          const yesterday = today.clone().subtract(1, 'day')
          return {
            from: yesterday.format('YYYY-MM-DD'),
            to: yesterday.format('YYYY-MM-DD'),
          }
        }
        case 'last_7_days':
          return {
            from: today.clone().subtract(6, 'days').format('YYYY-MM-DD'),
            to: today.format('YYYY-MM-DD'),
          }
        case 'last_14_days':
          return {
            from: today.clone().subtract(13, 'days').format('YYYY-MM-DD'),
            to: today.format('YYYY-MM-DD'),
          }
        case 'last_30_days':
          return {
            from: today.clone().subtract(29, 'days').format('YYYY-MM-DD'),
            to: today.format('YYYY-MM-DD'),
          }
        case 'last_60_days':
          return {
            from: today.clone().subtract(59, 'days').format('YYYY-MM-DD'),
            to: today.format('YYYY-MM-DD'),
          }
        case 'last_90_days':
          return {
            from: today.clone().subtract(89, 'days').format('YYYY-MM-DD'),
            to: today.format('YYYY-MM-DD'),
          }
        default:
          return { from: null, to: null }
      }
    },
    matchScheduleIntervalPreset(fromYmd, toYmd) {
      if (!fromYmd && !toYmd) {
        return ''
      }
      const presets = ['today', 'yesterday', 'last_7_days', 'last_14_days', 'last_30_days', 'last_60_days', 'last_90_days']
      for (let i = 0; i < presets.length; i += 1) {
        const range = this.resolveScheduleIntervalRange(presets[i])
        if (range.from === fromYmd && range.to === toYmd) {
          return presets[i]
        }
      }
      return 'custom'
    },
    syncScheduleIntervalFromDates() {
      if (this.applyingSchedulePreset) {
        return
      }
      const fromYmd = this.toYmd(this.scheduleFrom)
      const toYmd = this.toYmd(this.scheduleTo)
      this.scheduleInterval = this.matchScheduleIntervalPreset(fromYmd, toYmd)
    },
    onScheduleIntervalChange(value) {
      const preset = value === undefined || value === null ? this.scheduleInterval : value
      if (preset === 'custom') {
        return
      }
      if (!preset) {
        this.applyingSchedulePreset = true
        this.scheduleFrom = null
        this.scheduleTo = null
        this.$nextTick(() => {
          this.applyingSchedulePreset = false
          if (!this.filtersReady) return
          this.tablePage = 1
          this.reloadContent()
        })
        return
      }

      const range = this.resolveScheduleIntervalRange(preset)
      this.applyingSchedulePreset = true
      this.scheduleFrom = range.from
      this.scheduleTo = range.to
      this.$nextTick(() => {
        this.applyingSchedulePreset = false
        if (!this.filtersReady) return
        this.tablePage = 1
        this.reloadContent()
      })
    },
    onScheduleFromPicked(value) {
      const fromYmd = this.toYmd(value)
      const todayYmd = this.todayYmd()
      this.openToAfterFromClose = Boolean(fromYmd && todayYmd && fromYmd < todayYmd)

      // Fallback if @hidden does not fire (e.g. already closed).
      if (this.openToAfterFromClose) {
        window.setTimeout(() => {
          if (!this.openToAfterFromClose) {
            return
          }
          this.openToAfterFromClose = false
          this.openScheduleToPicker()
        }, 200)
      }
    },
    onScheduleFromHidden() {
      if (!this.openToAfterFromClose) {
        return
      }
      this.openToAfterFromClose = false
      this.$nextTick(() => {
        this.openScheduleToPicker()
      })
    },
    openScheduleToPicker() {
      const picker = this.$refs.scheduleToPicker
      if (!picker) {
        return
      }

      // b-form-datepicker has no public show(); open via dropdown control or toggle click.
      const control = picker.$refs && picker.$refs.control
      if (control && typeof control.show === 'function') {
        control.show()
        return
      }

      const toggle = control && control.$refs && control.$refs.toggle
      if (toggle && typeof toggle.click === 'function') {
        toggle.click()
        return
      }

      const button = picker.$el && picker.$el.querySelector('button')
      if (button) {
        button.click()
      }
    },
    buildContentFilters() {
      const params = {
        page: this.tablePage,
        per_page: this.pageLength,
      }
      const keyword = String(this.searchTerm || '').trim()
      if (keyword) {
        params.keyword = keyword
      }
      if (this.filterChannelId != null && this.filterChannelId !== '') {
        params.channel_id = this.filterChannelId
      }
      if (this.filterAssignedId != null && this.filterAssignedId !== '') {
        params.assigned = this.filterAssignedId
      }
      const fromYmd = this.toYmd(this.scheduleFrom)
      const toYmd = this.toYmd(this.scheduleTo)
      if (fromYmd) {
        params.schedule_from = fromYmd
      }
      if (toYmd) {
        params.schedule_to = toYmd
      }
      if (this.processingPostsOnly) {
        params.processing_only = 1
      }
      return params
    },
    async reloadContent() {
      const seq = ++this.loadSeq
      this.isTableLoading = true
      try {
        const result = await this.loadItems(this.buildContentFilters())
        if (seq !== this.loadSeq) return
        const total = Number(result?.total ?? 0)
        const maxPage = Math.max(1, Math.ceil(total / this.pageLength) || 1)
        if (this.tablePage > maxPage) {
          this.tablePage = maxPage
          return this.reloadContent()
        }
      } catch (e) {
        if (seq !== this.loadSeq) return
        toastFailure('Can not load content')
      } finally {
        if (seq === this.loadSeq) {
          this.isTableLoading = false
        }
      }
    },
    onPageChange(params) {
      this.tablePage = Number(params?.currentPage || 1)
      this.reloadContent()
    },
    onPerPageChange(n) {
      this.pageLength = Number(n) || 25
      this.tablePage = 1
      this.reloadContent()
    },
    resetFilters() {
      this.filtersReady = false
      this.searchTerm = ''
      this.scheduleFrom = null
      this.scheduleTo = null
      this.scheduleInterval = ''
      this.filterChannelId = null
      this.filterAssignedId = null
      this.processingPostsOnly = false
      this.tablePage = 1
      this.$nextTick(() => {
        this.filtersReady = true
        this.reloadContent()
      })
    },
    channelUrl(row) {
      const url = String(row.channel?.url || '').trim()
      return url || null
    },
    idKeyUrl(row) {
      const idKey = String(row.id_key || '').trim()
      if (!idKey) return null
      if (Number(row.social_resource_type_id) === 8) {
        return `https://www.youtube.com/watch?v=${encodeURIComponent(idKey)}`
      }
      return null
    },
    async copyTextToClipboard(text, successMessage) {
      const value = String(text || '').trim()
      if (!value) return
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(value)
        } else {
          const input = document.createElement('textarea')
          input.value = value
          document.body.appendChild(input)
          input.select()
          document.execCommand('copy')
          document.body.removeChild(input)
        }
        toastSuccess(successMessage)
      } catch (e) {
        toastFailure('Failed to copy')
      }
    },
    onContentIdKeyCopyClick(row) {
      if (this.contentIdKeyCopyTimer) {
        clearTimeout(this.contentIdKeyCopyTimer)
      }
      this.contentIdKeyCopyTimer = setTimeout(() => {
        this.contentIdKeyCopyTimer = null
        this.copyTextToClipboard(row.id_key, 'ID key copied')
      }, 250)
    },
    onContentIdKeyCopyDblClick(row) {
      if (this.contentIdKeyCopyTimer) {
        clearTimeout(this.contentIdKeyCopyTimer)
        this.contentIdKeyCopyTimer = null
      }
      const url = this.idKeyUrl(row)
      if (!url) {
        toastFailure('Content URL is not available')
        return
      }
      this.copyTextToClipboard(url, 'Content URL copied')
    },
    contentPreviewUrl(row) {
      const typeId = Number(row.social_resource_type_id)
      const idKey = String(row.id_key || '').trim()
      if (typeId === 8 && idKey) {
        return `https://img.youtube.com/vi/${encodeURIComponent(idKey)}/sddefault.jpg`
      }
      return row.social_resource_type?.icon || null
    },
    youtubeMaxResUrl(row) {
      const typeId = Number(row.social_resource_type_id)
      const idKey = String(row.id_key || '').trim()
      if (typeId !== 8 || !idKey) {
        return null
      }
      return `https://img.youtube.com/vi/${encodeURIComponent(idKey)}/maxresdefault.jpg`
    },
    contentNotes(row) {
      return String(row.notes || '').trim()
    },
    contentCategoryName(row) {
      return String(row?.category?.name || '').trim()
    },
    showDetailsButton(row) {
      return Boolean(this.youtubeMaxResUrl(row) || this.contentNotes(row))
    },
    detailsButtonLabel(row) {
      return this.contentNotes(row) ? 'Details and Notes' : 'Details'
    },
    postResourceIcon(post) {
      return post.resource?.social_resource_type?.icon || null
    },
    postResourceUrl(post) {
      const url = String(post.resource?.url || '').trim()
      return url || null
    },
    formatPostSchedule(post) {
      return formatCompanyDateTime(post.schedule)
    },
    truncatedPostName(post) {
      const name = String(post?.name || '')
      if (name.length <= 30) {
        return name
      }
      return `${name.slice(0, 30)}…`
    },
    postNameTooltip(post) {
      const name = String(post?.name || '')
      return name.length > 30 ? name : null
    },
    truncatedChannelName(row, emptyLabel = 'Channel') {
      const name = String(row?.channel?.name || '').trim()
      if (!name) {
        return emptyLabel
      }
      if (name.length <= 25) {
        return name
      }
      return `${name.slice(0, 25)}…`
    },
    channelNameTooltip(row) {
      const name = String(row?.channel?.name || '').trim()
      return name.length > 25 ? name : null
    },
    async copyPostName(post) {
      const name = String(post.name || '')
      if (!name) return
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(name)
        } else {
          const input = document.createElement('textarea')
          input.value = name
          document.body.appendChild(input)
          input.select()
          document.execCommand('copy')
          document.body.removeChild(input)
        }
        toastSuccess('Post name copied')
      } catch (e) {
        toastFailure('Failed to copy')
      }
    },
    openPreviewModal(row) {
      const hasNotes = Boolean(this.contentNotes(row))
      const typeId = Number(row.social_resource_type_id)
      const idKey = String(row.id_key || '').trim()
      this.previewTitle = row.name || (hasNotes ? 'Details and Notes' : 'Details')
      this.previewImageUrl = this.youtubeMaxResUrl(row)
      this.previewNotes = this.contentNotes(row)
      this.previewIsYoutube = typeId === 8
      this.previewYoutubeId = this.previewIsYoutube && idKey ? idKey : null
      this.previewPlaying = false
      this.showPreviewModal = true
    },
    playPreviewYoutube() {
      if (!this.previewYoutubeId) return
      this.previewPlaying = true
    },
    onPreviewModalHidden() {
      this.previewPlaying = false
      this.previewYoutubeId = null
      this.previewIsYoutube = false
      this.previewImageUrl = null
      this.previewNotes = ''
    },
    openModal(row) {
      this.record = row
      this.clickDate = new Date()
    },
    openAddPost(content) {
      this.postLockContentId = content.id
      this.postRecord = { crtcreator_content_id: content.id }
      this.postClickDate = new Date()
    },
    openEditPost(post) {
      this.postLockContentId = post.crtcreator_content_id || null
      this.postRecord = { ...post }
      this.postClickDate = new Date()
    },
    groupPostsIntoChains(posts) {
      const list = posts || []
      const ids = new Set(list.map((row) => Number(row.id)))
      const claimed = new Set()
      const chains = []
      const roots = list.filter((row) => {
        if (!row.previous_post_id) return true
        return !ids.has(Number(row.previous_post_id))
      })
      const orderIndex = new Map(list.map((row, index) => [Number(row.id), index]))
      roots
        .slice()
        .sort((a, b) => (orderIndex.get(Number(a.id)) ?? 0) - (orderIndex.get(Number(b.id)) ?? 0))
        .forEach((root) => {
          if (claimed.has(Number(root.id))) return
          const chain = this.buildContinuationChain(root, list)
          chain.forEach((row) => claimed.add(Number(row.id)))
          chains.push(chain)
        })
      list.forEach((row) => {
        if (claimed.has(Number(row.id))) return
        claimed.add(Number(row.id))
        chains.push([row])
      })
      return chains
    },
    visibleContentPosts(content) {
      const posts = content?.posts || []
      const entries = []
      this.groupPostsIntoChains(posts).forEach((chain) => {
        if (!chain.length) return
        const rootId = Number(chain[0].id)
        const expanded = Boolean(this.expandedContinuationRoots[rootId])
        const inChain = chain.length > 1
        const tip = chain[chain.length - 1]
        // Expanded playlist: last scheduled (tip) first, then earlier parts.
        const visible = !inChain || !expanded
          ? [tip]
          : [...chain].reverse()
        visible.forEach((post) => {
          const isTip = Number(post.id) === Number(tip.id)
          const chainIndex = chain.findIndex((row) => Number(row.id) === Number(post.id))
          // Playlist parts: original = 1), then 2), 3), … Only when there is a continuation chain.
          const partNumber = inChain && chainIndex >= 0 ? chainIndex + 1 : null
          entries.push({
            post,
            rootId,
            expanded,
            inChain,
            isTip,
            partNumber,
            showExpandToggle: inChain && isTip,
          })
        })
      })
      return entries
    },
    toggleContinuationExpand(rootId) {
      const key = Number(rootId)
      this.$set(
        this.expandedContinuationRoots,
        key,
        !this.expandedContinuationRoots[key],
      )
    },
    findContinuationRoot(post, posts) {
      const byId = new Map((posts || []).map((row) => [Number(row.id), row]))
      let root = post
      const seen = new Set()
      while (root?.previous_post_id) {
        const parentId = Number(root.previous_post_id)
        if (seen.has(parentId)) break
        seen.add(parentId)
        const parent = byId.get(parentId)
        if (!parent) break
        root = parent
      }
      return root
    },
    buildContinuationChain(post, posts) {
      const list = posts || []
      const root = this.findContinuationRoot(post, list)
      const chain = root ? [root] : []
      const seen = new Set(chain.map((row) => Number(row.id)))
      while (chain.length) {
        const tipId = Number(chain[chain.length - 1].id)
        const children = list
          .filter((row) => Number(row.previous_post_id) === tipId && !seen.has(Number(row.id)))
          .sort((a, b) => {
            const aSchedule = parseApiInstant(a.schedule)
            const bSchedule = parseApiInstant(b.schedule)
            if (aSchedule?.isValid() && bSchedule?.isValid() && !aSchedule.isSame(bSchedule)) {
              return aSchedule.valueOf() - bSchedule.valueOf()
            }
            return Number(a.id) - Number(b.id)
          })
        if (!children.length) break
        const next = children[children.length - 1]
        seen.add(Number(next.id))
        chain.push(next)
      }
      return chain
    },
    continuationBaseName(name) {
      return String(name || '').replace(/ - Part \d+$/i, '').trim()
    },
    nextContinuationSchedule(lastPost) {
      const now = moment.tz(companyTz())
      let next = parseApiInstant(lastPost?.schedule)
      if (!next || !next.isValid()) {
        return toUtcIso(now)
      }
      next = next.clone().add(4, 'hours')
      if (next.isBefore(now)) {
        next = now
      }
      return toUtcIso(next)
    },
    openContinuationPost(post, content) {
      const posts = content?.posts || []
      const chain = this.buildContinuationChain(post, posts)
      const root = chain[0] || post
      const last = chain[chain.length - 1] || post
      // Original = 0 → first continuation Part 2 (chain length 1 → Part 2)
      const partNumber = chain.length + 1
      const baseName = this.continuationBaseName(root.name || post.name)
      this.postLockContentId = post.crtcreator_content_id || content?.id || null
      this.postRecord = {
        name: `${baseName} - Part ${partNumber}`,
        caption: post.caption || '',
        hashtags: post.hashtags || '',
        assigned: post.assigned || null,
        crtcreator_resource_id: post.crtcreator_resource_id || null,
        crtcreator_content_id: post.crtcreator_content_id || content?.id || null,
        crtcreator_post_type_id: post.crtcreator_post_type_id || null,
        schedule: this.nextContinuationSchedule(last),
        password: post.password || '',
        previous_post_id: post.id,
        file_name: post.file_name || '',
      }
      this.postClickDate = new Date()
    },
    async onContentSaved() {
      try {
        await this.reloadContent()
      } catch (e) {
        toastFailure('Can not refresh content')
      }
    },
    async onPostSaved() {
      try {
        await this.reloadContent()
      } catch (e) {
        toastFailure('Can not refresh content')
      }
    },
    postHasUrl(post) {
      return Boolean(String(post?.url || '').trim())
    },
    postIsRemoved(post) {
      return Number(post?.removed) === 1
    },
    postHasCaption(post) {
      return Boolean(String(post?.caption || '').trim())
    },
    postHasHashtags(post) {
      return Boolean(String(post?.hashtags || '').trim())
    },
    postAssigneeName(post) {
      return String(post?.assignee?.name || '').trim()
    },
    postAssigneePicture(post) {
      if (!post?.assignee) {
        return null
      }
      return normalizeImageUrl(post.assignee.picture)
    },
    buildPostUpdatePayload(post, overrides = {}) {
      return {
        name: post.name,
        caption: post.caption || null,
        hashtags: post.hashtags || null,
        assigned: post.assigned || null,
        crtcreator_resource_id: post.crtcreator_resource_id || null,
        crtcreator_content_id: post.crtcreator_content_id || null,
        crtcreator_post_type_id: post.crtcreator_post_type_id || null,
        id_key: post.id_key || null,
        url: post.url || null,
        schedule: post.schedule || null,
        password: post.password || null,
        previous_post_id: post.previous_post_id || null,
        file_name: post.file_name || null,
        removed: Number(post.removed) || 0,
        ...overrides,
      }
    },
    markPostRemoved(post) {
      if (this.postIsRemoved(post)) {
        toastFailure('Post is already removed')
        return
      }
      this.$swal({
        title: 'Post removed?',
        text: 'This will mark the post as removed.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, remove',
      }).then((result) => {
        if (!result.isConfirmed) return
        this.updatePost({
          id: post.id,
          payload: this.buildPostUpdatePayload(post, { removed: 1 }),
        })
          .then(() => {
            toastSuccess('Post removed')
            return this.reloadContent()
          })
          .catch(() => toastFailure('Failed to remove post'))
      })
    },
    restorePostRow(post) {
      if (!this.postIsRemoved(post)) {
        toastFailure('Post is not removed')
        return
      }
      this.$swal({
        title: 'Restore this post?',
        text: 'This will restore the post.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, restore',
      }).then((result) => {
        if (!result.isConfirmed) return
        this.updatePost({
          id: post.id,
          payload: this.buildPostUpdatePayload(post, { removed: 0 }),
        })
          .then(() => {
            toastSuccess('Post restored')
            return this.reloadContent()
          })
          .catch(() => toastFailure('Failed to restore post'))
      })
    },
    deletePostRow(post) {
      this.$swal({
        title: 'Delete this post?',
        text: 'This will delete the post',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete',
      }).then((result) => {
        if (!result.isConfirmed) return
        this.deletePost(post.id)
          .then(() => {
            toastSuccess('Post deleted')
            return this.reloadContent()
          })
          .catch(() => toastFailure('Failed to delete post'))
      })
    },
    deleteRow(row) {
      this.$swal({
        title: 'Are you sure?',
        text: 'This content will be deleted.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete',
      }).then((result) => {
        if (!result.isConfirmed) return
        this.deleteItem(row.id)
          .then(() => {
            toastSuccess('Deleted')
            return this.reloadContent()
          })
          .catch(() => toastFailure('Failed to delete'))
      })
    },
  },
}
</script>

<style scoped>
::v-deep .content-name-col {
  width: 28%;
  max-width: 28%;
}

.content-schedule-filter {
  width: 10.25rem;
  min-width: 10.25rem;
  max-width: 10.25rem;
  flex: 0 0 10.25rem;
}

.content-schedule-filter ::v-deep .b-form-btn-label-control.form-control,
.content-schedule-filter ::v-deep .form-control {
  width: 100%;
  min-width: 0;
  min-height: 31px;
  padding-top: 0.2rem;
  padding-bottom: 0.2rem;
  font-size: 0.8125rem;
}

.content-schedule-filter ::v-deep .form-control > .btn-content-wrapper,
.content-schedule-filter ::v-deep .b-form-btn-label-control > .form-control {
  min-width: 0;
}

.content-schedule-interval {
  min-width: 10.5rem;
}

.content-schedule-interval ::v-deep .custom-select,
.content-schedule-interval ::v-deep .form-control {
  font-size: 0.8125rem;
  min-height: 31px;
  padding-top: 0.2rem;
  padding-bottom: 0.2rem;
}

.content-channel-filter {
  min-width: 22rem;
  width: 22rem;
}

.content-channel-filter ::v-deep .image-text-select {
  width: 100%;
}

.content-name-text {
  font-size: 0.8125rem;
  line-height: 1.3;
}

.content-category-label {
  display: inline-block;
  max-width: 100%;
  padding: 0.1rem 0.4rem;
  border-radius: 0.2rem;
  background: #6e6b7b;
  color: #fff;
  font-size: 0.65rem;
  font-weight: 500;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.content-channel-header {
  gap: 0.35rem;
}

.content-channel-type-icon {
  width: 20px;
  height: 20px;
  object-fit: contain;
  flex-shrink: 0;
}

.content-channel-name {
  font-size: 0.75rem;
  line-height: 1.2;
  color: #6e6b7b;
}

a.content-channel-name:hover {
  color: #5e5873;
}

.content-channel-id-key {
  padding-top: 5px;
  font-size: 0.8125rem;
  line-height: 1.2;
  color: #7367f0;
}

.content-channel-id-key-row {
  gap: 0.15rem;
}

.content-channel-id-key-copy {
  flex-shrink: 0;
  margin-top: 5px;
}

.content-channel-id-key--muted {
  color: #b9b9c3;
}

.content-posts-cell {
  display: block;
  width: 100%;
}

.content-post-row {
  width: 100%;
  min-height: 1.35rem;
  margin-bottom: 0.25rem;
  gap: 0.5rem;
  font-size: 0.78rem;
  line-height: 1.25;
  padding: 0.15rem 0;
  border-radius: 0.25rem;
  transition: background-color 0.15s ease;
}

.content-post-row:hover {
  background-color: #f3f2f7;
}

.content-post-row--continuation {
  opacity: 0.92;
}

.content-post-main {
  flex: 1 1 auto;
  min-width: 0;
  gap: 0.2rem;
  justify-content: flex-start;
}

.content-post-expand-icon {
  width: 14px;
  height: 14px;
}

.content-post-expand-icon:hover {
  color: #7367f0 !important;
}

.content-post-continue-icon,
.content-post-continue-spacer {
  width: 14px;
  height: 14px;
  margin-left: 0.25rem;
  margin-right: 0.25rem;
  flex-shrink: 0;
  display: inline-block;
}

.content-post-type-icon {
  width: 14px;
  height: 14px;
  object-fit: contain;
  flex-shrink: 0;
}

.content-post-assignee-avatar {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.content-assigned-filter {
  min-width: 11rem;
}

.content-post-resource-name,
.content-post-name,
.content-post-type-name {
  min-width: 0;
}

.content-post-part {
  color: #000;
  font-weight: 600;
  margin-right: 0.15rem;
}

.content-post-type-name {
  color: #5e5873;
}

.content-post-name--empty-key {
  color: #b9b9c3;
}

.content-post-name--has-key {
  color: #7367f0;
}

a.content-post-name:hover {
  text-decoration: underline;
}

.content-post-removed {
  text-decoration: line-through;
  opacity: 0.7;
}

.content-post-actions {
  flex-shrink: 0;
  margin-left: auto;
  justify-content: flex-end;
}

.content-post-schedule {
  font-size: 0.72rem;
  white-space: nowrap;
  color: #5e5873;
}

.content-details-notes {
  white-space: pre-wrap;
}

.content-preview-youtube {
  position: relative;
  width: 100%;
  padding-bottom: 56.25%;
  height: 0;
  overflow: hidden;
  background: #000;
}

.content-preview-youtube iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 0;
}

.white-space-pre-wrap {
  white-space: pre-wrap;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
